import { useCallback, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler } from 'react-hook-form';

import { IDemoSignUpParams } from '@aqx-core/common/api/user-account';
import { useBaseForm } from '@aqx-core/common/hooks/form/use-base-form';
import { createSignUpDemoSchema } from '@aqx-core/common/schemas/sign-up-demo-schema';
import { useFirstDemoTenantProduct } from '@aqx-core/common/services/company';
import { useSignUpDemoDefaultDeposit } from '@aqx-core/common/services/sign-up-demo';
import { signUpDemoViewController } from '@aqx-core/common/services/sign-up-demo/controller';
import { EUISnackBarMessageType, snackbarController } from '@aqx-core/common/services/snackbar';

export interface ISignUpDemoValues extends Omit<IDemoSignUpParams, 'phoneNumber'> {
  dialCode: string;
  localPhoneNumber: string;
  agree: boolean;
}

export const useSignUpDemoForm = ({ defaultValues }: { defaultValues?: Partial<ISignUpDemoValues> } = {}) => {
  const { isCentroidDemo } = useFirstDemoTenantProduct();
  const schema = useMemo(() => createSignUpDemoSchema({ isCentroidDemo }), [isCentroidDemo]);

  const resolver = useMemo(() => yupResolver(schema), [schema]);
  const useFormMethods = useBaseForm<ISignUpDemoValues>({
    resolver,
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false
  });
  const defaultDeposit = useSignUpDemoDefaultDeposit();
  const { handleSubmit, trigger, watch, formState, setValue } = useFormMethods;

  const [name, firstName, lastName, email, dialCode, localPhoneNumber, depositAmount] = watch([
    'name',
    'firstName',
    'lastName',
    'email',
    'dialCode',
    'localPhoneNumber',
    'depositAmount'
  ]);

  const {
    errors: {
      name: nameError,
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      dialCode: dialCodeError,
      localPhoneNumber: localPhoneNumberError,
      depositAmount: depositAmountError
    }
  } = formState;

  const isDisabled = useMemo(() => {
    const nameValidation = isCentroidDemo
      ? !firstName || !lastName || firstNameError || lastNameError
      : !name || nameError;

    return !!(
      nameValidation ||
      !email ||
      !dialCode ||
      !localPhoneNumber ||
      !depositAmount ||
      emailError ||
      dialCodeError ||
      localPhoneNumberError ||
      depositAmountError
    );
  }, [
    isCentroidDemo,
    name,
    nameError,
    firstName,
    firstNameError,
    lastName,
    lastNameError,
    email,
    emailError,
    dialCode,
    dialCodeError,
    localPhoneNumber,
    localPhoneNumberError,
    depositAmount,
    depositAmountError
  ]);

  const onError = useCallback((message: string) => {
    snackbarController.showAlert({
      message,
      messageType: EUISnackBarMessageType.ERROR
    });
  }, []);

  const handleFormSubmit: SubmitHandler<ISignUpDemoValues> = useCallback(
    (values) => {
      const { dialCode, localPhoneNumber, ...rest } = values;
      const phoneNumber = `${dialCode} ${localPhoneNumber}`;

      signUpDemoViewController.openDemoAccount({ params: { phoneNumber, ...rest }, onError });
    },
    [onError]
  );

  const submit = useCallback(
    (e?: React.FormEvent<HTMLFormElement>) => {
      handleSubmit(handleFormSubmit)(e);
    },
    [handleSubmit, handleFormSubmit]
  );

  useEffect(() => {
    if (defaultDeposit) {
      setValue('depositAmount', defaultDeposit);
    }
  }, [defaultDeposit, setValue]);

  useEffect(() => {
    if (dialCode && localPhoneNumber) {
      trigger('localPhoneNumber');
    }
  }, [dialCode, localPhoneNumber, trigger]);

  return {
    depositAmount,
    dialCode,
    email,
    firstName,
    isDisabled,
    lastName,
    localPhoneNumber,
    name,
    useFormMethods,
    submit,
    trigger
  };
};
